import { createContext, useContext, useEffect, useState } from "react";

const dict = {
  en: {
    nav: {
      services: "Services",
      stack: "Tech Stack",
      why: "Why PRONET",
      contact: "Contact",
      cta: "Book an audit",
    },
    hero: {
      eyebrow: "Network Management & IT Infrastructure for corporates & hospitality",
      l1: "Seamless control.",
      l2: "Total security.",
      l3: "Zero downtime.",
      sub: "PRONET designs, monitors and fully manages network infrastructure for corporates and hotels across Europe and the Middle East — so your business never feels the network.",
      cta1: "Schedule a Network Audit",
      cta2: "Explore our services",
      scroll: "Scroll",
      stackLabel: "Engineered on",
      stackBrands: "Ubiquiti UniFi · Cisco · HP · WISP",
    },
    noc: {
      title: "Network Operations",
      status: "All systems nominal",
      uptime: "Uptime",
      latency: "Latency",
      throughput: "Throughput",
      note: "Illustrative NOC view",
    },
    marquee: [
      "24/7 Proactive Monitoring",
      "Network Operations",
      "Guest Wi-Fi Excellence",
      "WISP Management",
      "Cybersecurity Compliance",
      "Cross-Border Support",
      "UniFi Architecture",
      "Zero-Downtime Deployments",
    ],
    services: {
      eyebrow: "Our Services",
      h2: "Two industries. One standard of excellence.",
      body: "From enterprise backbones to hotel guest Wi-Fi, we own the complexity so owners never do. You get one accountable partner for the entire lifecycle of your network.",
      c1tag: "Corporate",
      c1title: "Corporate Network Solutions",
      c1items: [
        "Enterprise network setup & configuration",
        "Cybersecurity compliance",
        "24/7 remote monitoring",
        "Complete infrastructure management",
      ],
      c1stack: "Cisco · HP",
      c1link: "Discuss your infrastructure",
      c2tag: "Hospitality",
      c2title: "Hospitality & Hotel Solutions",
      c2items: [
        "High-speed guest Wi-Fi management",
        "Bandwidth allocation & QoS",
        "WISP & carrier-grade wireless",
        "Seamless multi-property connectivity",
      ],
      c2stack: "Ubiquiti UniFi · WISP",
      c2link: "Upgrade your guest experience",
    },
    stack: {
      eyebrow: "Technology",
      h2: "Built on hardware the world trusts.",
      body: "We design, deploy and manage on the platforms below — selected for reliability, scale and security, and operated by specialists who know them down to the firmware.",
      cards: [
        {
          name: "Ubiquiti UniFi",
          tag: "Core Platform",
          desc: "Hospitality-grade Wi-Fi, switching and gateways — our core platform for hotels and WISP estates.",
        },
        {
          name: "Cisco",
          tag: "Enterprise",
          desc: "Enterprise switching, routing and security for demanding corporate environments.",
        },
        {
          name: "HP",
          tag: "Workplace",
          desc: "Trusted enterprise infrastructure from HP for the modern corporate workplace.",
        },
        {
          name: "WISP Setups",
          tag: "Carrier-Grade",
          desc: "Wireless ISP architectures — point-to-point links, outdoor coverage and subscriber management.",
        },
      ],
      visualTag: "Wide-area WISP",
      visualTitle: "Vast estates. Seamless coverage.",
    },
    why: {
      eyebrow: "Why PRONET",
      h2: "The quiet advantage behind busy networks.",
      c1title: "24/7 proactive monitoring",
      c1body: "Our network operations centre watches every link, switch and access point around the clock. We detect and resolve before your guests or staff ever notice.",
      live: "Monitoring live",
      c2title: "Cross-border support",
      c2body: "One team across Europe and the UAE. Local response, international standards, a single accountable partner.",
      c3title: "Strict data security",
      c3body: "GDPR-aligned processes and hardened network segmentation keep your data — and your guests' — private.",
      c4title: "Hassle-free management",
      c4body: "Full lifecycle ownership: design, deployment, monitoring, expansion. Owners get one contract and complete peace of mind.",
      pipeline: ["Design", "Deployment", "Monitoring", "Expansion"],
    },
    clients: {
      eyebrow: "Case Studies",
      h2: "Trusted by resorts and hotels across two seas.",
      body: "From the Indian Ocean to the Mediterranean and the Alps, PRONET keeps guest connectivity flawless in some of the world's most demanding resort locations.",
      groups: [
        {
          label: "Indian Ocean",
          items: [
            { name: "Blue Bay Village", loc: "Watamu, Kenya" },
            { name: "Dongwe Resort", loc: "Zanzibar" },
            { name: "Hotel Cote D'Or", loc: "Praslin, Seychelles" },
          ],
        },
        {
          label: "Mediterranean · Sardinia",
          items: [
            { name: "iGV Club Santagiusta", loc: "Sardinia, Italy" },
            { name: "iGV Club Santaclara", loc: "Sardinia, Italy" },
            { name: "Naim Boutique Hotel", loc: "Sardinia, Italy" },
            { name: "Albaruja Hotel", loc: "Sardinia, Italy" },
          ],
        },
        {
          label: "Alps · Trentino",
          items: [{ name: "Hotel Relais Des Alpes", loc: "Trentino, Italy" }],
        },
      ],
    },
    contact: {
      eyebrow: "Contact",
      h2: "Let's secure your network.",
      body: "Tell us about your property or business — a network specialist will reply within one business day.",
      emailL: "Email",
      hqL: "Headquarters",
      hq: "Building A1, Dubai Digital Park, Dubai Silicon Oasis, Dubai",
      covL: "Coverage",
      cov: "Europe · Middle East · Worldwide",
      respL: "Response",
      resp: "Within one business day",
      name: "Full name",
      company: "Company",
      email: "Work email",
      msg: "What do you need?",
      consent:
        "I consent to PRONET processing my personal data in line with the Privacy Policy (GDPR) in order to respond to my enquiry.",
      submit: "Request a quote",
      sending: "Sending...",
      toast: "Request received — we'll be in touch within one business day.",
      toastErr: "Please complete all fields and accept the data consent.",
      toastFail: "Something went wrong — please try again or email us directly.",
    },
    cookies: {
      text: "We use only essential technical preferences (language, consent) — no tracking, no third-party cookies.",
      accept: "Accept",
      decline: "Reject",
      policy: "Privacy Policy",
    },
    privacy: {
      back: "Back to site",
      title: "Privacy Policy",
      updated: "Last updated: October 2026",
      intro: "How we handle your personal data — simple, transparent and GDPR-compliant.",
      sections: [
        {
          h: "Data controller",
          p: 'PRONET ("we", "us"), based in the United Arab Emirates, operates this website. For any privacy question, write to wifi@pronet-uae.com.',
        },
        {
          h: "Data we collect",
          p: "When you use our contact form we process the data you provide: name, company, email address and message. We also store a record of your consent to the processing.",
        },
        {
          h: "Purpose & legal basis",
          p: "Your data is used solely to answer your enquiry and prepare a quote (Art. 6(1)(a) and (f) GDPR — consent and legitimate interest).",
        },
        {
          h: "Retention",
          p: "Enquiry data is kept only as long as needed to handle your request and any follow-up, then deleted.",
        },
        {
          h: "Data sharing",
          p: "We never sell or share your data for marketing purposes. It is processed by our form service solely to deliver our reply.",
        },
        {
          h: "Your rights",
          p: "You have the right to access, rectify, erase, restrict and object to processing, and to data portability. Write to wifi@pronet-uae.com to exercise any right. You may also lodge a complaint with your local supervisory authority.",
        },
        {
          h: "Cookies",
          p: "We use only functional local storage for your language and cookie-consent choices. No analytics or tracking cookies are set without your consent.",
        },
      ],
    },
    footer: {
      tagline: "Smart networking solutions.",
      rights: "All rights reserved.",
      gdpr: "GDPR-compliant",
    },
  },
  it: {
    nav: {
      services: "Servizi",
      stack: "Tech Stack",
      why: "Perché PRONET",
      contact: "Contatti",
      cta: "Prenota un audit",
    },
    hero: {
      eyebrow: "Gestione di rete & Infrastruttura IT per aziende e hospitality",
      l1: "Controllo totale.",
      l2: "Sicurezza assoluta.",
      l3: "Zero downtime.",
      sub: "PRONET progetta, monitora e gestisce completamente l'infrastruttura di rete di aziende e hotel in Europa e Medio Oriente — perché la tua attività non debba mai pensarci.",
      cta1: "Prenota un audit di rete",
      cta2: "Scopri i servizi",
      scroll: "Scorri",
      stackLabel: "Costruita su",
      stackBrands: "Ubiquiti UniFi · Cisco · HP · WISP",
    },
    noc: {
      title: "Operazioni di rete",
      status: "Tutti i sistemi nominali",
      uptime: "Disponibilità",
      latency: "Latenza",
      throughput: "Throughput",
      note: "Vista NOC illustrativa",
    },
    marquee: [
      "Monitoraggio proattivo 24/7",
      "Operazioni di rete",
      "Wi-Fi guest d'eccellenza",
      "Gestione WISP",
      "Conformità cybersecurity",
      "Supporto transfrontaliero",
      "Architettura UniFi",
      "Deploy senza fermi",
    ],
    services: {
      eyebrow: "I nostri servizi",
      h2: "Due settori. Un unico standard di eccellenza.",
      body: "Dal backbone aziendale al Wi-Fi degli hotel: gestiamo noi la complessità, così i proprietari non devono occuparsene. Un unico partner responsabile per l'intero ciclo di vita della rete.",
      c1tag: "Corporate",
      c1title: "Soluzioni di rete per aziende",
      c1items: [
        "Setup e configurazione di reti enterprise",
        "Conformità cybersecurity",
        "Monitoraggio remoto 24/7",
        "Gestione completa dell'infrastruttura",
      ],
      c1stack: "Cisco · HP",
      c1link: "Parla della tua infrastruttura",
      c2tag: "Hospitality",
      c2title: "Soluzioni per hotel e hospitality",
      c2items: [
        "Gestione Wi-Fi guest ad alta velocità",
        "Allocazione banda & QoS",
        "Wireless WISP di qualità carrier",
        "Connettività multi-struttura senza interruzioni",
      ],
      c2stack: "Ubiquiti UniFi · WISP",
      c2link: "Migliora l'esperienza degli ospiti",
    },
    stack: {
      eyebrow: "Tecnologia",
      h2: "Costruiti su hardware di fiducia mondiale.",
      body: "Progettiamo, implementiamo e gestiamo sulle piattaforme seguenti — scelte per affidabilità, scalabilità e sicurezza, gestite da specialisti che le conoscono fino al firmware.",
      cards: [
        {
          name: "Ubiquiti UniFi",
          tag: "Piattaforma centrale",
          desc: "Wi-Fi, switching e gateway di livello hospitality — la nostra piattaforma centrale per hotel e WISP.",
        },
        {
          name: "Cisco",
          tag: "Enterprise",
          desc: "Switching, routing e sicurezza enterprise per ambienti aziendali esigenti.",
        },
        {
          name: "HP",
          tag: "Workplace",
          desc: "Infrastruttura enterprise HP affidabile per il luogo di lavoro moderno.",
        },
        {
          name: "Setup WISP",
          tag: "Qualità carrier",
          desc: "Architetture wireless per ISP — collegamenti point-to-point, copertura outdoor e gestione abbonati.",
        },
      ],
      visualTag: "WISP su larga area",
      visualTitle: "Ampie estensioni. Copertura senza interruzioni.",
    },
    why: {
      eyebrow: "Perché PRONET",
      h2: "Il vantaggio silenzioso dietro le reti più intenshe.",
      c1title: "Monitoraggio proattivo 24/7",
      c1body: "Il nostro centro operazioni di rete sorveglia ogni collegamento, switch e access point 24 ore su 24. Interveniamo prima che ospiti o collaboratori se ne accorgano.",
      live: "Monitoraggio attivo",
      c2title: "Supporto transfrontaliero",
      c2body: "Un solo team tra Europa ed EAU. Risposta locale, standard internazionali, un unico partner responsabile.",
      c3title: "Sicurezza dei dati rigorosa",
      c3body: "Processi conformi al GDPR e segmentazione di rete rinforzata mantengono privati i tuoi dati — e quelli dei tuoi ospiti.",
      c4title: "Gestione senza pensieri",
      c4body: "Proprietà completa del ciclo di vita: progettazione, implementazione, monitoraggio, espansione. I proprietari hanno un solo contratto e piena tranquillità.",
      pipeline: ["Progettazione", "Implementazione", "Monitoraggio", "Espansione"],
    },
    clients: {
      eyebrow: "Casi di successo",
      h2: "La fiducia di resort e hotel tra due mari.",
      body: "Dall'Oceano Indiano al Mediterraneo e alle Alpi, PRONET mantiene una connettività guest impeccabile in alcune delle località turistiche più esigenti al mondo.",
      groups: [
        {
          label: "Oceano Indiano",
          items: [
            { name: "Blue Bay Village", loc: "Watamu, Kenya" },
            { name: "Dongwe Resort", loc: "Zanzibar" },
            { name: "Hotel Cote D'Or", loc: "Praslin, Seychelles" },
          ],
        },
        {
          label: "Mediterraneo · Sardegna",
          items: [
            { name: "iGV Club Santagiusta", loc: "Sardegna, Italia" },
            { name: "iGV Club Santaclara", loc: "Sardegna, Italia" },
            { name: "Naim Boutique Hotel", loc: "Sardegna, Italia" },
            { name: "Albaruja Hotel", loc: "Sardegna, Italia" },
          ],
        },
        {
          label: "Alpi · Trentino",
          items: [{ name: "Hotel Relais Des Alpes", loc: "Trentino, Italia" }],
        },
      ],
    },
    contact: {
      eyebrow: "Contatti",
      h2: "Mettiamo al sicuro la tua rete.",
      body: "Raccontaci la tua struttura o azienda — uno specialista di rete ti risponderà entro un giorno lavorativo.",
      emailL: "Email",
      hqL: "Sede",
      hq: "Building A1, Dubai Digital Park, Dubai Silicon Oasis, Dubai",
      covL: "Copertura",
      cov: "Europa · Medio Oriente · Mondo",
      respL: "Risposta",
      resp: "Entro un giorno lavorativo",
      name: "Nome completo",
      company: "Azienda",
      email: "Email aziendale",
      msg: "Di cosa hai bisogno?",
      consent:
        "Acconsenso al trattamento dei miei dati personali da parte di PRONET conformemente alla Privacy Policy (GDPR) per rispondere alla mia richiesta.",
      submit: "Richiedi un preventivo",
      sending: "Invio...",
      toast: "Richiesta ricevuta — ti risponderemo entro un giorno lavorativo.",
      toastErr: "Compila tutti i campi e accetta il consenso dati.",
      toastFail: "Qualcosa è andato storto — riprova o scrivici direttamente.",
    },
    cookies: {
      text: "Usiamo solo preferenze tecniche essenziali (lingua e consenso) — niente tracciamento né cookie di terze parti.",
      accept: "Accetta",
      decline: "Rifiuta",
      policy: "Privacy Policy",
    },
    privacy: {
      back: "Torna al sito",
      title: "Privacy Policy",
      updated: "Ultimo aggiornamento: ottobre 2026",
      intro: "Come trattiamo i tuoi dati personali — in modo semplice, trasparente e conforme al GDPR.",
      sections: [
        {
          h: "Titolare del trattamento",
          p: 'PRONET ("noi") con sede negli Emirati Arabi Uniti gestisce questo sito. Per qualsiasi domanda sulla privacy scrivi a wifi@pronet-uae.com.',
        },
        {
          h: "Dati che raccogliamo",
          p: "Quando compili il modulo contatti trattiamo i dati che ci fornisci: nome, azienda, indirizzo email e messaggio. Conserviamo anche la prova del consenso al trattamento.",
        },
        {
          h: "Finalità e base giuridica",
          p: "Usiamo i tuoi dati esclusivamente per rispondere alla richiesta e preparare un preventivo (art. 6, par. 1, lett. a e f GDPR — consenso e interesse legittimo).",
        },
        {
          h: "Conservazione",
          p: "I dati delle richieste sono conservati solo per il tempo necessario a gestire la richiesta ed eventuali follow-up, poi cancellati.",
        },
        {
          h: "Condivisione dei dati",
          p: "Non vendiamo né cediamo i dati a terzi per finalità di marketing. Vengono elaborati dal nostro servizio di form esclusivamente per consegnare la nostra risposta.",
        },
        {
          h: "I tuoi diritti",
          p: "Hai diritto di accesso, rettifica, cancellazione, limitazione e opposizione al trattamento, oltre alla portabilità dei dati. Per esercitarli scrivi a wifi@pronet-uae.com. Puoi inoltre presentare reclamo all'autorità di controllo del tuo paese.",
        },
        {
          h: "Cookie",
          p: "Usiamo solo archiviazione locale funzionale per le preferenze di lingua e di consenso cookie. Nessun cookie analitico o di tracciamento senza il tuo consenso.",
        },
      ],
    },
    footer: {
      tagline: "Smart networking solutions.",
      rights: "Tutti i diritti riservati.",
      gdpr: "Conforme al GDPR",
    },
  },
  de: {
    nav: {
      services: "Leistungen",
      stack: "Tech-Stack",
      why: "Warum PRONET",
      contact: "Kontakt",
      cta: "Audit buchen",
    },
    hero: {
      eyebrow: "Netzwerkmanagement & IT-Infrastruktur für Unternehmen & Hospitality",
      l1: "Nahtlose Kontrolle.",
      l2: "Volle Sicherheit.",
      l3: "Kein Ausfall.",
      sub: "PRONET plant, überwacht und managt Netzwerkinfrastruktur für Unternehmen und Hotels in Europa und dem Nahen Osten — damit Ihr Geschäft das Netzwerk nie spürt.",
      cta1: "Netzwerk-Audit vereinbaren",
      cta2: "Leistungen entdecken",
      scroll: "Scrollen",
      stackLabel: "Gebaut auf",
      stackBrands: "Ubiquiti UniFi · Cisco · HP · WISP",
    },
    noc: {
      title: "Netzwerkbetrieb",
      status: "Alle Systeme nominal",
      uptime: "Verfügbarkeit",
      latency: "Latenz",
      throughput: "Durchsatz",
      note: "Illustrative NOC-Ansicht",
    },
    marquee: [
      "24/7 Proaktives Monitoring",
      "Netzwerkbetrieb",
      "Guest-WLAN auf Premium-Niveau",
      "WISP-Management",
      "Cybersecurity-Compliance",
      "Grenzüberschreitender Support",
      "UniFi-Architektur",
      "Einsatz ohne Ausfallzeit",
    ],
    services: {
      eyebrow: "Leistungen",
      h2: "Zwei Branchen. Ein Anspruch an Exzellenz.",
      body: "Vom Enterprise-Backbone bis zum Hotel-Guest-WLAN: Wir übernehmen die Komplexität, damit Eigentümer sich um nichts kümmern müssen. Ein verantwortlicher Partner für den gesamten Lebenszyklus Ihres Netzwerks.",
      c1tag: "Corporate",
      c1title: "Netzwerklösungen für Unternehmen",
      c1items: [
        "Enterprise-Netzwerk-Setup & -Konfiguration",
        "Cybersecurity-Compliance",
        "24/7-Remote-Monitoring",
        "Vollständiges Infrastruktur-Management",
      ],
      c1stack: "Cisco · HP",
      c1link: "Infrastruktur besprechen",
      c2tag: "Hospitality",
      c2title: "Lösungen für Hotels & Hospitality",
      c2items: [
        "Hochgeschwindigkeits-Guest-WLAN-Management",
        "Bandbreitenallokation & QoS",
        "WISP & Carrier-Grade-Funktechnik",
        "Nahtlose Multi-Property-Konnektivität",
      ],
      c2stack: "Ubiquiti UniFi · WISP",
      c2link: "Gast-Erlebnis upgraden",
    },
    stack: {
      eyebrow: "Technologie",
      h2: "Gebaut auf Hardware, der die Welt vertraut.",
      body: "Wir planen, deployen und managen auf den folgenden Plattformen — gewählt für Zuverlässigkeit, Skalierung und Sicherheit, betrieben von Spezialisten, die sie bis zur Firmware kennen.",
      cards: [
        {
          name: "Ubiquiti UniFi",
          tag: "Kernplattform",
          desc: "Hospitality-taugliches WLAN, Switching und Gateways — unsere Kernplattform für Hotels und WISPs.",
        },
        {
          name: "Cisco",
          tag: "Enterprise",
          desc: "Enterprise-Switching, Routing und Sicherheit für anspruchsvolle Unternehmensumgebungen.",
        },
        {
          name: "HP",
          tag: "Arbeitsplatz",
          desc: "Zuverlässige Enterprise-Infrastruktur von HP für den modernen Arbeitsplatz.",
        },
        {
          name: "WISP-Setups",
          tag: "Carrier-Grade",
          desc: "Funkarchitekturen für ISPs — Point-to-Point-Links, Außenabdeckung und Teilnehmer-Management.",
        },
      ],
      visualTag: "Flächendeckendes WISP",
      visualTitle: "Weite Areale. Nahtlose Abdeckung.",
    },
    why: {
      eyebrow: "Warum PRONET",
      h2: "Der stille Vorteil hinter ausgelasteten Netzwerken.",
      c1title: "24/7 proaktives Monitoring",
      c1body: "Unser Network Operations Center überwacht jede Verbindung, jeden Switch und jeden Access Point rund um die Uhr. Wir erkennen und beheben, bevor Gäste oder Mitarbeiter es je bemerken.",
      live: "Monitoring aktiv",
      c2title: "Grenzüberschreitender Support",
      c2body: "Ein Team in Europa und den VAE. Lokale Reaktion, internationale Standards, ein einziger verantwortlicher Partner.",
      c3title: "Strenge Datensicherheit",
      c3body: "DSGVO-konforme Prozesse und gehärtete Netzwerksegmentierung halten Ihre Daten — und die Ihrer Gäste — privat.",
      c4title: "Sorgloses Management",
      c4body: "Volle Verantwortung über den gesamten Lebenszyklus: Planung, Betrieb, Monitoring, Ausbau. Eigentümer erhalten einen Vertrag und absolute Ruhe.",
      pipeline: ["Planung", "Deployment", "Monitoring", "Ausbau"],
    },
    clients: {
      eyebrow: "Referenzen",
      h2: "Gefragt bei Resorts und Hotels zwischen zwei Meeren.",
      body: "Vom Indischen Ozean bis zum Mittelmeer und den Alpen: PRONET hält die Gästekonnektivität an einigen der anspruchsvollsten Resortstandorten der Welt makellos.",
      groups: [
        {
          label: "Indischer Ozean",
          items: [
            { name: "Blue Bay Village", loc: "Watamu, Kenia" },
            { name: "Dongwe Resort", loc: "Sansibar" },
            { name: "Hotel Cote D'Or", loc: "Praslin, Seychellen" },
          ],
        },
        {
          label: "Mittelmeer · Sardinien",
          items: [
            { name: "iGV Club Santagiusta", loc: "Sardinien, Italien" },
            { name: "iGV Club Santaclara", loc: "Sardinien, Italien" },
            { name: "Naim Boutique Hotel", loc: "Sardinien, Italien" },
            { name: "Albaruja Hotel", loc: "Sardinien, Italien" },
          ],
        },
        {
          label: "Alpen · Trentino",
          items: [{ name: "Hotel Relais Des Alpes", loc: "Trentino, Italien" }],
        },
      ],
    },
    contact: {
      eyebrow: "Kontakt",
      h2: "Sichern wir Ihr Netzwerk.",
      body: "Erzählen Sie uns von Ihrem Unternehmen oder Objekt — ein Netzwerk-Spezialist antwortet innerhalb eines Werktags.",
      emailL: "E-Mail",
      hqL: "Hauptsitz",
      hq: "Building A1, Dubai Digital Park, Dubai Silicon Oasis, Dubai",
      covL: "Abdeckung",
      cov: "Europa · Naher Osten · Weltweit",
      respL: "Antwort",
      resp: "Innerhalb eines Werktags",
      name: "Vollständiger Name",
      company: "Unternehmen",
      email: "Geschäftliche E-Mail",
      msg: "Was benötigen Sie?",
      consent:
        "Ich stimme zu, dass PRONET meine personenbezogenen Daten gemäß der Datenschutzerklärung (DSGVO) zur Beantwortung meiner Anfrage verarbeitet.",
      submit: "Angebot anfragen",
      sending: "Wird gesendet...",
      toast: "Anfrage erhalten — wir melden uns innerhalb eines Werktags.",
      toastErr: "Bitte alle Felder ausfüllen und der Datenzustimmung zustimmen.",
      toastFail: "Etwas ist schiefgelaufen — bitte erneut versuchen oder schreiben Sie uns direkt.",
    },
    cookies: {
      text: "Wir verwenden nur technische Präferenzen (Sprache, Einwilligung) — kein Tracking, keine Cookies von Drittanbietern.",
      accept: "Akzeptieren",
      decline: "Ablehnen",
      policy: "Datenschutzerklärung",
    },
    privacy: {
      back: "Zurück zur Website",
      title: "Datenschutzerklärung",
      updated: "Zuletzt aktualisiert: Oktober 2026",
      intro: "Wie wir Ihre personenbezogenen Daten behandeln — einfach, transparent und DSGVO-konform.",
      sections: [
        {
          h: "Verantwortlicher",
          p: 'PRONET ("wir") mit Sitz in den Vereinigten Arabischen Emiraten betreibt diese Website. Bei Datenschutzfragen schreiben Sie an wifi@pronet-uae.com.',
        },
        {
          h: "Welche Daten wir erheben",
          p: "Wenn Sie das Kontaktformular nutzen, verarbeiten wir die von Ihnen angegebenen Daten: Name, Unternehmen, E-Mail-Adresse und Nachricht. Die Einwilligung in die Verarbeitung speichern wir als Nachweis.",
        },
        {
          h: "Zweck und Rechtsgrundlage",
          p: "Wir verwenden Ihre Daten ausschließlich zur Beantwortung Ihrer Anfrage und zur Erstellung eines Angebots (Art. 6 Abs. 1 lit. a und f DSGVO — Einwilligung und berechtigtes Interesse).",
        },
        {
          h: "Speicherdauer",
          p: "Anfragedaten werden nur so lange aufbewahrt, wie zur Bearbeitung der Anfrage und etwaiger Nachfragen erforderlich, und anschließend gelöscht.",
        },
        {
          h: "Datenweitergabe",
          p: "Wir verkaufen oder übertragen Ihre Daten nicht zu Marketingzwecken. Sie werden von unserem Formulardienst ausschließlich zur Zustellung unserer Antwort verarbeitet.",
        },
        {
          h: "Ihre Rechte",
          p: "Sie haben Recht auf Auskunft, Berichtigung, Löschung, Einschränkung und Widerspruch sowie auf Datenübertragbarkeit. Wenden Sie sich an wifi@pronet-uae.com. Zudem können Sie sich bei Ihrer örtlichen Aufsichtsbehörde beschweren.",
        },
        {
          h: "Cookies",
          p: "Wir nutzen nur funktionalen lokalen Speicher für Sprach- und Cookie-Einwilligungen. Keine Analyse- oder Tracking-Cookies ohne Ihre Einwilligung.",
        },
      ],
    },
    footer: {
      tagline: "Smart networking solutions.",
      rights: "Alle Rechte vorbehalten.",
      gdpr: "DSGVO-konform",
    },
  },
  fr: {
    nav: {
      services: "Services",
      stack: "Stack techno",
      why: "Pourquoi PRONET",
      contact: "Contact",
      cta: "Réserver un audit",
    },
    hero: {
      eyebrow: "Gestion réseau & infrastructure IT pour entreprises & hôtellerie",
      l1: "Contrôle total.",
      l2: "Sécurité absolue.",
      l3: "Zéro coupure.",
      sub: "PRONET conçoit, surveille et gère intégralement l'infrastructure réseau d'entreprises et d'hôtels à travers l'Europe et le Moyen-Orient — pour que votre activité n'y pense jamais.",
      cta1: "Planifier un audit réseau",
      cta2: "Découvrir nos services",
      scroll: "Défiler",
      stackLabel: "Construit sur",
      stackBrands: "Ubiquiti UniFi · Cisco · HP · WISP",
    },
    noc: {
      title: "Opérations réseau",
      status: "Tous systèmes nominaux",
      uptime: "Disponibilité",
      latency: "Latence",
      throughput: "Débit",
      note: "Vue NOC illustrative",
    },
    marquee: [
      "Supervision proactive 24/7",
      "Opérations réseau",
      "Wi-Fi client d'exception",
      "Gestion WISP",
      "Conformité cybersécurité",
      "Support transfrontalier",
      "Architecture UniFi",
      "Déploiements sans interruption",
    ],
    services: {
      eyebrow: "Nos services",
      h2: "Deux industries. Une seule exigence d'excellence.",
      body: "Du backbone d'entreprise au Wi-Fi des hôtels, nous assumons toute la complexité — les propriétaires n'ont rien à gérer. Un partenaire unique et responsable pour tout le cycle de vie de votre réseau.",
      c1tag: "Corporate",
      c1title: "Solutions réseaux corporate",
      c1items: [
        "Déploiement et configuration d'entreprise",
        "Conformité cybersécurité",
        "Supervision à distance 24/7",
        "Gestion complète de l'infrastructure",
      ],
      c1stack: "Cisco · HP",
      c1link: "Discuter de votre infrastructure",
      c2tag: "Hospitality",
      c2title: "Solutions hôtellerie & hospitality",
      c2items: [
        "Gestion du Wi-Fi haut débit pour clients",
        "Allocation de bande passante & QoS",
        "Architectures WISP de qualité opérateur",
        "Connectivité multi-établissements sans faille",
      ],
      c2stack: "Ubiquiti UniFi · WISP",
      c2link: "Élever l'expérience client",
    },
    stack: {
      eyebrow: "Technologie",
      h2: "Construit sur le matériel en qui le monde a confiance.",
      body: "Nous concevons, déployons et gérons sur les plateformes ci-dessous — choisies pour leur fiabilité, leur échelle et leur sécurité, opérées par des spécialistes qui les connaissent jusqu'au firmware.",
      cards: [
        {
          name: "Ubiquiti UniFi",
          tag: "Plateforme centrale",
          desc: "Wi-Fi, switching et passerelles de niveau hospitalité — notre plateforme centrale pour hôtels et WISP.",
        },
        {
          name: "Cisco",
          tag: "Entreprise",
          desc: "Switching, routage et sécurité d'entreprise pour les environnements corporate exigeants.",
        },
        {
          name: "HP",
          tag: "Lieu de travail",
          desc: "Infrastructure d'entreprise HP de confiance pour le lieu de travail moderne.",
        },
        {
          name: "WISP",
          tag: "Qualité opérateur",
          desc: "Architectures sans fil pour FAI — liaisons point à point, couverture extérieure et gestion des abonnés.",
        },
      ],
      visualTag: "WISP étendu",
      visualTitle: "De vastes domaines. Une couverture sans faille.",
    },
    why: {
      eyebrow: "Pourquoi PRONET",
      h2: "L'avantage discret derrière les réseaux les plus occupés.",
      c1title: "Supervision proactive 24/7",
      c1body: "Notre centre d'opérations réseau surveille chaque liaison, switch et point d'accès jour et nuit. Nous détectons et résolvons avant que vos clients ou équipes ne s'en aperçoivent.",
      live: "Supervision en direct",
      c2title: "Support transfrontalier",
      c2body: "Une seule équipe entre l'Europe et les Émirats. Réponse locale, standards internationaux, un partenaire unique et responsable.",
      c3title: "Sécurité des données stricte",
      c3body: "Des processus conformes au RGPD et une segmentation réseau renforcée préservent vos données — et celles de vos clients.",
      c4title: "Gestion sans souci",
      c4body: "Maîtrise complète du cycle de vie : conception, déploiement, supervision, extension. Les propriétaires reçoivent un seul contrat et une tranquillité totale.",
      pipeline: ["Conception", "Déploiement", "Supervision", "Extension"],
    },
    clients: {
      eyebrow: "Références",
      h2: "La confiance de resorts et d'hôtels entre deux mers.",
      body: "De l'océan Indien à la Méditerranée et aux Alpes, PRONET garantit une connectivité client impeccable dans certains des sites balnéaires les plus exigeants au monde.",
      groups: [
        {
          label: "Océan Indien",
          items: [
            { name: "Blue Bay Village", loc: "Watamu, Kenya" },
            { name: "Dongwe Resort", loc: "Zanzibar" },
            { name: "Hotel Cote D'Or", loc: "Praslin, Seychelles" },
          ],
        },
        {
          label: "Méditerranée · Sardaigne",
          items: [
            { name: "iGV Club Santagiusta", loc: "Sardaigne, Italie" },
            { name: "iGV Club Santaclara", loc: "Sardaigne, Italie" },
            { name: "Naim Boutique Hotel", loc: "Sardaigne, Italie" },
            { name: "Albaruja Hotel", loc: "Sardaigne, Italie" },
          ],
        },
        {
          label: "Alpes · Trentin",
          items: [{ name: "Hotel Relais Des Alpes", loc: "Trentin, Italie" }],
        },
      ],
    },
    contact: {
      eyebrow: "Contact",
      h2: "Sécurisons votre réseau.",
      body: "Parlez-nous de votre établissement ou entreprise — un spécialiste réseau vous répond sous un jour ouvré.",
      emailL: "E-mail",
      hqL: "Siège",
      hq: "Building A1, Dubai Digital Park, Dubai Silicon Oasis, Dubai",
      covL: "Couverture",
      cov: "Europe · Moyen-Orient · Monde",
      respL: "Réponse",
      resp: "Sous un jour ouvré",
      name: "Nom complet",
      company: "Entreprise",
      email: "E-mail professionnel",
      msg: "De quoi avez-vous besoin ?",
      consent:
        "J'accepte que PRONET traite mes données personnelles conformément à la politique de confidentialité (RGPD) afin de répondre à ma demande.",
      submit: "Demander un devis",
      sending: "Envoi...",
      toast: "Demande reçue — nous vous répondrons sous un jour ouvré.",
      toastErr: "Veuillez remplir tous les champs et accepter le consentement des données.",
      toastFail: "Une erreur est survenue — réessayez ou écrivez-nous directement.",
    },
    cookies: {
      text: "Nous utilisons uniquement des préférences techniques (langue, consentement) — aucun suivi ni cookie tiers.",
      accept: "Accepter",
      decline: "Refuser",
      policy: "Politique de confidentialité",
    },
    privacy: {
      back: "Retour au site",
      title: "Politique de confidentialité",
      updated: "Dernière mise à jour : octobre 2026",
      intro: "Comment nous traitons vos données personnelles — simplement, en toute transparence et conformément au RGPD.",
      sections: [
        {
          h: "Responsable du traitement",
          p: "PRONET (« nous »), établi aux Émirats Arabes Unis, exploite ce site. Pour toute question relative aux données, écrivez à wifi@pronet-uae.com.",
        },
        {
          h: "Données collectées",
          p: "Lorsque vous utilisez le formulaire de contact, nous traitons les données que vous fournissez : nom, entreprise, adresse e-mail et message. Nous conservons également la preuve de votre consentement.",
        },
        {
          h: "Finalité et base légale",
          p: "Vos données servent uniquement à répondre à votre demande et à préparer un devis (art. 6, §1, a et f du RGPD — consentement et intérêt légitime).",
        },
        {
          h: "Conservation",
          p: "Les données des demandes sont conservées le temps nécessaire au traitement de la demande et de ses suites, puis supprimées.",
        },
        {
          h: "Partage des données",
          p: "Nous ne vendons ni ne cédons vos données à des fins marketing. Elles sont traitées par notre service de formulaire uniquement pour délivrer notre réponse.",
        },
        {
          h: "Vos droits",
          p: "Vous disposez de droits d'accès, de rectification, d'effacement, de limitation et d'opposition, ainsi que de portabilité. Écrivez à wifi@pronet-uae.com. Vous pouvez aussi saisir l'autorité de contrôle de votre pays.",
        },
        {
          h: "Cookies",
          p: "Nous utilisons uniquement le stockage local fonctionnel pour la langue et le consentement cookies. Aucun cookie analytique ou de suivi sans votre consentement.",
        },
      ],
    },
    footer: {
      tagline: "Smart networking solutions.",
      rights: "Tous droits réservés.",
      gdpr: "Conforme RGPD",
    },
  },
};

const detectLang = () => {
  const candidates =
    (navigator.languages && navigator.languages.length > 0
      ? navigator.languages
      : [navigator.language || "en"]);
  for (const c of candidates) {
    const base = String(c).slice(0, 2).toLowerCase();
    if (dict[base]) return base;
  }
  return "en";
};

const LangContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    let saved = null;
    try {
      saved = window.localStorage.getItem("pronet-lang");
    } catch (e) {}
    return saved && dict[saved] ? saved : detectLang();
  });

  const setLang = (l) => {
    setLangState(l);
    try {
      window.localStorage.setItem("pronet-lang", l);
    } catch (e) {}
  };

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  return <LangContext.Provider value={{ lang, setLang, t: dict[lang] }}>{children}</LangContext.Provider>;
}

export const useLanguage = () => useContext(LangContext);
